/**
 * Platform Purchase Abstraction
 *
 * Handles purchases differently on web (Stripe) vs native.
 * On native the bridge dispatches to either:
 *   - flutter_stripe Payment Sheet  (when stripeOptions is provided)
 *   - RevenueCat IAP                (default, when stripeOptions is absent)
 *
 * For the Stripe path the web layer (PaymentModal) is responsible for
 * creating the PaymentIntent / SetupIntent via the existing backend endpoint
 * and passing the resulting clientSecret down through stripeOptions.
 * This keeps all business logic (product IDs, coupons, bookings) in the web
 * and uses Flutter purely as a native Payment Sheet host.
 *
 * Entitlement creation is always handled server-side via Stripe / RevenueCat
 * webhooks, never by the client.
 */

import { bridge, BridgeChannels, BridgeActions } from '../bridge';
import { api } from '../api';

export type PurchaseResult =
  | { success: true }
  | { success: false; cancelled: true; error?: never }
  | { success: false; cancelled?: false; error: string };

/** Passed from the payment modal to trigger the native Stripe Payment Sheet. */
export interface NativeStripeOptions {
  /** client_secret from the PaymentIntent or SetupIntent created by the backend. */
  clientSecret: string;
  /** Tells flutter_stripe which Payment Sheet parameter to populate. */
  intentType: 'payment_intent' | 'setup_intent';
}

export interface PurchasePlatform {
  /**
   * Check if native purchase flow should be used.
   * When true, use purchaseProduct() instead of the Stripe web payment modal.
   */
  shouldUseNativePurchase(): boolean;

  /**
   * Purchase a specific product by its identifier.
   * @param productId     Product identifier (e.g. "sub_1_month")
   * @param bookingId     Optional booking ID to associate with the purchase
   * @param stripeOptions When provided, the native Stripe Payment Sheet is used
   *                      instead of RevenueCat IAP. The web layer must have
   *                      already created the intent and pass the clientSecret here.
   */
  purchaseProduct(
    productId: string,
    bookingId?: string,
    stripeOptions?: NativeStripeOptions,
  ): Promise<PurchaseResult>;
}

/**
 * Web implementation — Stripe is handled by the PaymentModal component directly.
 * purchaseProduct() should never be reached on web.
 */
class WebPurchase implements PurchasePlatform {
  shouldUseNativePurchase(): boolean {
    return false;
  }

  async purchaseProduct(
    _productId: string,
    _bookingId?: string,
    _stripeOptions?: NativeStripeOptions,
  ): Promise<PurchaseResult> {
    return { success: false, error: 'Native purchase not available on web' };
  }
}

/**
 * Poll for booking activation after native purchase.
 * The webhook may take a moment to process, so we retry until activated or timeout.
 */
async function pollForBookingActivation(
  bookingId: string,
  maxWaitMs: number = 20000,
  intervalMs: number = 1000,
): Promise<boolean> {
  const startTime = Date.now();

  while (Date.now() - startTime < maxWaitMs) {
    try {
      const response = await api.post<{ activated: boolean }>(
        `/user/reservations/${bookingId}/activate`,
      );

      if (response.success && response.data?.activated) {
        console.log('[NativePurchase] Booking activated successfully');
        return true;
      }

      console.log('[NativePurchase] Booking not yet activated, retrying...');
    } catch (e) {
      console.error('[NativePurchase] Activation poll error:', e);
    }

    await new Promise(resolve => setTimeout(resolve, intervalMs));
  }

  console.warn('[NativePurchase] Booking activation timed out');
  return false;
}

/**
 * Native implementation.
 *
 * When stripeOptions is provided the bridge payload includes the clientSecret
 * and intentType so Flutter can present the Payment Sheet without making any
 * additional network calls itself.
 * Without stripeOptions the payload routes to RevenueCat IAP.
 */
class NativePurchase implements PurchasePlatform {
  shouldUseNativePurchase(): boolean {
    return true;
  }

  async purchaseProduct(
    productId: string,
    bookingId?: string,
    stripeOptions?: NativeStripeOptions,
  ): Promise<PurchaseResult> {
    try {
      const payload: Record<string, unknown> = {
        productId,
        bookingId,
        paymentMethod: stripeOptions ? 'stripe' : 'iap',
        ...(stripeOptions && {
          clientSecret: stripeOptions.clientSecret,
          intentType: stripeOptions.intentType,
        }),
      };

      const result = await bridge.request<PurchaseResult>(
        BridgeChannels.PURCHASE,
        BridgeActions.PURCHASE_PRODUCT,
        payload,
        0, // no timeout — IAP / Payment Sheet can stay open indefinitely
      );

      // Poll for booking activation: webhook (Stripe or RevenueCat) needs a
      // moment to create the entitlement before the booking can be activated.
      if (result.success && bookingId) {
        const activated = await pollForBookingActivation(bookingId);
        if (!activated) {
          console.warn('[NativePurchase] Purchase succeeded but booking activation timed out');
        }
      }

      return result;
    } catch (e) {
      console.error('[NativePurchase] purchaseProduct error:', e);
      return { success: false, error: String(e) };
    }
  }
}

const webPurchase = new WebPurchase();
const nativePurchase = new NativePurchase();

export function getPurchasePlatform(): PurchasePlatform {
  return bridge.isNative ? nativePurchase : webPurchase;
}
